/**
 * Conference sales data loader for third sheet
 * Reads sales data per client per conference per year
 */

import { YEAR_CONFIG } from '../../config/yearConfig';

/**
 * Build configured conference sales loader years as numbers.
 * @returns {number[]}
 */
export const getConfiguredConferenceSalesLoaderYears = () => {
  const start = Number(YEAR_CONFIG.baseYear);
  const end = Number(YEAR_CONFIG.lastDataYear);

  if (!Number.isInteger(start) || !Number.isInteger(end) || start > end) {
    return [];
  }

  const years = [];
  for (let year = start; year <= end; year++) {
    years.push(year);
  }
  return years;
};

/**
 * Transform raw conference sales data into structured format
 * @param {Array} rawData - Raw data from Excel third sheet
 * @returns {Object} Structured data organized by client name and sponsor name
 */
const transformConferenceSalesData = (rawData) => {
  const salesData = {};
  
  // Years to process from config boundaries
  const years = getConfiguredConferenceSalesLoaderYears();
  
  rawData.forEach(row => {
    const customerName = row['Customer rename'] || '';
    const conferenceName = row['ConferenceName'] || '';
    const groupName = row['GroupName rename'] || '';
    
    // Skip rows without required data
    if (!customerName || !conferenceName || !groupName) {
      return;
    }
    
    // Create unique sponsor key by combining conference name and group name
    // This matches the display format in SponsorCarousel: "{conferenceName} {groupName}"
    const sponsorKey = `${conferenceName} ${groupName}`;
    
    // Initialize nested structure if needed
    if (!salesData[customerName]) {
      salesData[customerName] = {};
    }
    
    if (!salesData[customerName][sponsorKey]) {
      salesData[customerName][sponsorKey] = {};
    }
    
    // Parse yearly data
    years.forEach(year => {
      const roomsKey = `${year} Rooms`;
      const rnKey = `${year} RN`;
      const revenueKey = `${year} Revenue`;
      
      // Parse values, treating empty/missing as 0
      const rooms = parseFloat(row[roomsKey]) || 0;
      const rn = parseFloat(row[rnKey]) || 0;
      const revenue = parseFloat(row[revenueKey]) || 0;
      
      // Store year data using the full sponsor key
      salesData[customerName][sponsorKey][year] = {
        rooms,
        rn,
        revenue
      };
    });
  });
  
  return salesData;
};

/**
 * Load and transform conference sales data from Excel file (third sheet)
 * @param {Object} workbook - ExcelJS workbook object
 * @returns {Promise<Object>} The conference sales data object
 */
export const loadConferenceSalesData = async (workbook) => {
  try {
    // The third sheet should be at index 2 (0-based)
    const worksheet = workbook.worksheets[2];
    
    if (!worksheet) {
      const availableSheets = workbook.worksheets.map(ws => ws.name).join(', ');
      throw new Error('Third sheet not found in Excel file. Available sheets: ' + availableSheets);
    }
    
    // Convert to JSON format similar to xlsx library
    const rawData = [];
    const headers = [];
    
    // Get headers from first row
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      headers[colNumber - 1] = cell.value ? String(cell.value) : '';
    });
    
    // Process data rows
    worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
      if (rowNumber === 1) return; // Skip header row
      
      const rowData = {};
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        const header = headers[colNumber - 1];
        if (header) {
          // Convert cell value to string to preserve formatting
          rowData[header] = cell.value != null ? String(cell.value) : '';
        }
      });
      
      // Only add rows that have at least one non-empty value
      if (Object.values(rowData).some(val => val !== '')) {
        rawData.push(rowData);
      }
    });
    
    // Transform the data
    const transformedData = transformConferenceSalesData(rawData);
    
    return transformedData;
  } catch (error) {
    console.error('Error loading conference sales data from Excel file:', error);
    // Return empty object on error to prevent app crashes
    return {};
  }
};
